import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_FundTypeRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbFundType} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Fund_Type
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {FundTypeBOValidations} from "./FundTypeBO.Validations";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {AuditService} from "../PA.Core/Security/AuditService";
import {FundTypeBOAudit} from "./FundTypeBO.Audit";

export interface IFundTypeLoadOptions {
    dbContext?: PAdbContext
}

const FundTypeLoadOptions_default: IFundTypeLoadOptions = {}

export class FundTypeBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_FundType: WB_FundTypeRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New FundType'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_FundType = undefined;
    }

    //</editor-fold>

    get WB_FundType(): WB_FundTypeRow {
        return this.drWB_FundType;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating FundType");

        let internalOnComplete = new Promise<IBusinessObject>( (resolve, error) => {

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                var sError = "Unable to determine dbContext for new FundType.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Fund_Type)
                .then((newFundTypeRow: WB_FundTypeRow) => {
                    newFundTypeRow.setDefaults()
                    self.drWB_FundType = newFundTypeRow;
                    self.dbContext.add(esWB_Fund_Type, newFundTypeRow)
                    self.Identity.setAll(self.WB_FundType.cif.WB_FUNDTYPE_ID, self.WB_FundType.cif.FUNDTYPE_ID, self.WB_FundType.cif.FUNDTYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_FundType, 'WB_FUNDTYPE_ID', 'FUNDTYPE_ID', 'FUNDTYPE_NAME')


                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return  new Promise<IBusinessObject>( (resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("FundType already loaded.")
                resolve(self); // "FundType already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, FundTypeLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for FundType.")
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = FundTypeBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = FundTypeBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aFundTypeRow: any = recordResolver.get(esWB_Fund_Type, anId)

                    if (_.isEmpty(aFundTypeRow)) {
                        error("FundType not Found");
                        return;
                    }

                    self.drWB_FundType = aFundTypeRow;
                    self.Identity.setAll(self.WB_FundType.cif.WB_FUNDTYPE_ID, self.WB_FundType.cif.FUNDTYPE_ID, self.WB_FundType.cif.FUNDTYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_FundType, 'WB_FUNDTYPE_ID', 'FUNDTYPE_ID', 'FUNDTYPE_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_FundType.cif.FundType_NAME + "(" + self.WB_FundType.cif.FundType_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate FundType.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            FundTypeBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })



            self._bIsValidated = true;
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbFundType = <IwbFundType>self.drWB_FundType;
        this.SaveProgressProvider.start("Saving " + wbFundType.FUNDTYPE_NAME + "(" + wbFundType.FUNDTYPE_ID + ")");
        let auditService: AuditService = undefined
        let bDone = false

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save FundType.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                }) .then((ignore) => {
                if (bDone) return
                return FundTypeBOAudit.FundTypeAuditBO(self)

            })
                .then((anAuditService) => {
                    auditService = anAuditService
                    return;
                })

                    saveFunction(esWB_Fund_Type, self.drWB_FundType, {})
                        .then((ignore) => {
                            var tasks: Promise<any>[] = []
                            return Promise.all(tasks)
                        })
                        .then((ignored) => {
                            if (bBatch) {
                                self.dbContext.addBatchCompleteListener(() => {
                                    self._bIsSaved = true;
                                })
                            } else {
                                self._bIsSaved = true;
                            }
                            self.SaveProgressProvider.finish();
                            resolve(self);
                        })
                        .catch((err) => {
                            self.SaveProgressProvider.exception(err);
                            reject(self)
                        }).then((ignored) => {
                        if (auditService) {
                            return auditService.Save(bBatch)
                        }
                    })
                // })


            // Add code to do individual BO save for dbContextRows....



        });
    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbFundType = <IwbFundType>self.drWB_FundType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbFundType.FUNDTYPE_NAME + "(" + wbFundType.WB_FUNDTYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the FundType

            // 2. If Valid, mark all the records for deletion.
            self.drWB_FundType.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

//</editor-fold>


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
            },
            dctIncludeBelongsToNamedRelations: {
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Fund_Type,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }


};
PAEnvironment.registerBusinessObjectClass( FundTypeBO , "FundTypeBO", "Fund Type")